import { atkJobData } from "common/state/atkJobData";
import {
  Dispatch,
  isAction,
  Middleware,
  ThunkDispatch,
  UnknownAction,
} from "@reduxjs/toolkit";
import {
  GLOBAL_ID,
  JobWebsocketRootState,
  jobWebsocketSlice,
  SubscribableTarget,
  SubscriptionCounts,
  SubscriptionTarget,
} from "./jobWebsocketSlice";
import {
  JobServiceSubscriptionMessage,
  JobServiceSubscriptionRequest,
  SubscriptionRequestType,
  UpdateType,
} from "protos/rtc/web_socket";
import { rtcJobsApi } from "common/state/rtcJobsApi";
import { State } from "protos/rtc/jobs";

type MiddlewareState = JobWebsocketRootState & {
  atkJobData: ReturnType<typeof atkJobData.reducer>;
};

// Thunk-capable dispatch so the middleware can fire RTK Query `initiate` thunks
// for the initial seed while still dispatching plain slice actions.
type SeedDispatch = ThunkDispatch<MiddlewareState, unknown, UnknownAction>;

// Interventions worth seeding on subscribe — everything except COMPLETED.
const ACTIVE_INTERVENTION_STATES = [
  State.ACKNOWLEDGED,
  State.CANCELLED,
  State.FAILED,
  State.IN_PROGRESS,
  State.NEW,
  State.PAUSED,
  State.PENDING,
  State.READY,
  State.STATE_UNSPECIFIED,
  State.UNRECOGNIZED,
].join(",");

const subscribedRobotSerials = (subs: SubscriptionCounts): string[] =>
  Object.keys(subs[SubscriptionTarget.ROBOT]);

// Walk every (target, id) subscription with its refcount.
const forEachSubscription = (
  subs: SubscriptionCounts,
  cb: (target: SubscribableTarget, id: string, count: number) => void
): void => {
  for (const key of Object.keys(subs)) {
    const target = Number(key) as SubscribableTarget;
    for (const [id, count] of Object.entries(subs[target])) {
      cb(target, id, count);
    }
  }
};

// Run a one-shot RTK Query and resolve to its data, or undefined on any error
// (e.g. 404). Errors are swallowed: the live subscription is the source of
// truth, the seed is only a best-effort initial value.
// Several job endpoints also return 404 for expected-null values (active task, checklist, etc), and those are
// also intentionally swallowed.
const fetchOnce = async <T>(result: {
  unwrap: () => Promise<T>;
}): Promise<T | undefined> => {
  try {
    return await result.unwrap();
  } catch {
    return undefined;
  }
};

// Seed the store from REST when a subscription is first established, acting as
// if the records arrived over the subscription. Seeding is set-only and never
// clears: the subscribe and this fetch race, so a live update may land first —
// clearing on a seed miss would clobber fresher data. The store starts empty,
// so there is nothing to clear anyway.
const seedSubscription = async (
  target: SubscribableTarget,
  id: string,
  dispatch: SeedDispatch
): Promise<void> => {
  const jobsEndpoints = rtcJobsApi.endpoints;
  const opts = { subscribe: false, forceRefetch: true };

  switch (target) {
    case SubscriptionTarget.JOB: {
      const job = await fetchOnce(
        dispatch(jobsEndpoints.getJob.initiate({ id }, opts))
      );
      if (job) {
        dispatch(atkJobData.actions.setJob({ job }));
      }
      break;
    }
    case SubscriptionTarget.OBJECTIVE: {
      // OBJECTIVE fans out: the objective itself plus its ordered tasks.
      const objectiveId = Number.parseInt(id);
      const [objective, tasksRes] = await Promise.all([
        fetchOnce(
          dispatch(
            jobsEndpoints.getObjective.initiate({ id: objectiveId }, opts)
          )
        ),
        fetchOnce(
          dispatch(
            jobsEndpoints.listObjectiveTasks.initiate({ objectiveId }, opts)
          )
        ),
      ]);
      if (objective) {
        dispatch(atkJobData.actions.setObjective({ objective }));
      }
      if (tasksRes) {
        for (const task of tasksRes.tasks) {
          dispatch(atkJobData.actions.setTask({ task }));
        }
      }
      break;
    }
    case SubscriptionTarget.TASK: {
      const task = await fetchOnce(
        dispatch(
          jobsEndpoints.getTask.initiate({ id: Number.parseInt(id) }, opts)
        )
      );
      if (task) {
        dispatch(atkJobData.actions.setTask({ task }));
      }
      break;
    }
    case SubscriptionTarget.ROBOT: {
      // ROBOT fans out: active task, active objective, and assignments. The
      // wire stream pushes checklist/exemptions on subscribe, so those aren't
      // seeded here.
      const serial = id;
      const [activeTask, activeObjective, assignmentsRes] = await Promise.all([
        fetchOnce(
          dispatch(jobsEndpoints.getActiveAtomicTask.initiate({ serial }, opts))
        ),
        fetchOnce(
          dispatch(jobsEndpoints.getActiveObjective.initiate({ serial }, opts))
        ),
        fetchOnce(
          dispatch(
            jobsEndpoints.listAssignments.initiate(
              { robotSerials: [serial] },
              opts
            )
          )
        ),
      ]);
      if (activeTask) {
        dispatch(
          atkJobData.actions.setActiveTask({ serial, task: activeTask })
        );
      }
      if (activeObjective) {
        dispatch(
          atkJobData.actions.setActiveObjective({
            serial,
            objective: activeObjective,
          })
        );
      }
      if (assignmentsRes) {
        dispatch(
          atkJobData.actions.seedGlobalAssignments({
            assignments: assignmentsRes.assignments,
          })
        );
      }
      break;
    }
    case SubscriptionTarget.INTERVENTIONS: {
      // Robot-scoped when a serial is present, otherwise the global feed.
      const params =
        id === GLOBAL_ID
          ? { states: ACTIVE_INTERVENTION_STATES }
          : { robotSerials: id, states: ACTIVE_INTERVENTION_STATES };
      const res = await fetchOnce(
        dispatch(jobsEndpoints.listInterventions.initiate(params, opts))
      );
      if (res) {
        dispatch(
          atkJobData.actions.seedGlobalInterventions({
            interventions: res.interventions,
          })
        );
      }
      break;
    }
  }
};

const buildWireMessage = (
  target: SubscribableTarget,
  id: string,
  requestType: SubscriptionRequestType
): JobServiceSubscriptionRequest => {
  switch (target) {
    case SubscriptionTarget.JOB: {
      return { type: requestType, target, jobId: Number.parseInt(id) };
    }
    case SubscriptionTarget.JOB_CONFIG: {
      return { type: requestType, target, jobId: Number.parseInt(id) };
    }
    case SubscriptionTarget.OBJECTIVE: {
      return { type: requestType, target, objectiveId: Number.parseInt(id) };
    }
    case SubscriptionTarget.TASK: {
      return { type: requestType, target, taskId: Number.parseInt(id) };
    }
    case SubscriptionTarget.ROBOT: {
      return { type: requestType, target, robotSerial: id };
    }
    case SubscriptionTarget.INTERVENTIONS: {
      // Robot-scoped carries the serial; the global feed omits it.
      return id === GLOBAL_ID
        ? { type: requestType, target }
        : { type: requestType, target, robotSerial: id };
    }
  }
};

const routeMessage = (
  message: JobServiceSubscriptionMessage,
  dispatch: Dispatch,
  getState: () => MiddlewareState
): void => {
  if (message.job) {
    const { job, type } = message.job;
    if (!job) {
      return;
    }
    if (type === UpdateType.UPDATE_TYPE_DELETE) {
      dispatch(atkJobData.actions.clearJob({ jobId: job.id }));
    } else {
      dispatch(atkJobData.actions.setJob({ job }));
    }
  }

  // BLOCKED — job config routing. A JobConfiguration is just a oneof of the
  // per-type config payloads with no identity of its own, so we can't route a
  // config to the right job until it carries a job_id. This handler is ready to
  // go; un-comment it once all of these have landed:
  //   1. protos#917 — adds `job_id` to JobConfiguration
  //      (https://github.com/carbonrobotics/protos/pull/917)
  //   2. rtc#955 — populates it
  //      (https://github.com/carbonrobotics/rtc/pull/955)
  //   3. the protos submodule here is bumped so
  //      `message.jobConfig.configuration.jobId` exists
  //   4. atkJobData gains `setJobConfig({ configuration })` and
  //      `clearJobConfig({ jobId })` (cloud-side job config slice work)
  // if (message.jobConfig) {
  //   const { configuration, type } = message.jobConfig;
  //   if (!configuration) {
  //     return;
  //   }
  //   if (type === UpdateType.UPDATE_TYPE_DELETE) {
  //     dispatch(
  //       atkJobData.actions.clearJobConfig({ jobId: configuration.jobId })
  //     );
  //   } else {
  //     dispatch(atkJobData.actions.setJobConfig({ configuration }));
  //   }
  // }

  if (message.objective) {
    const { objective, type } = message.objective;
    if (!objective) {
      return;
    }
    if (type === UpdateType.UPDATE_TYPE_DELETE) {
      dispatch(
        atkJobData.actions.clearObjective({ objectiveId: objective.id })
      );
    } else {
      dispatch(atkJobData.actions.setObjective({ objective }));
    }
  }

  if (message.task) {
    const { task, type } = message.task;
    if (!task) {
      return;
    }
    if (type === UpdateType.UPDATE_TYPE_DELETE) {
      dispatch(atkJobData.actions.clearTask({ taskId: task.id }));
    } else {
      dispatch(atkJobData.actions.setTask({ task }));
    }
  }

  if (message.activeObjective) {
    const { objective, type } = message.activeObjective;
    // Clear on explicit DELETE or when server signals "no active" via id=0
    const normalized =
      type === UpdateType.UPDATE_TYPE_DELETE || !objective?.id
        ? undefined
        : objective;
    for (const serial of subscribedRobotSerials(
      getState().jobWebsocket.subscriptions
    )) {
      if (normalized) {
        dispatch(
          atkJobData.actions.setActiveObjective({
            serial,
            objective: normalized,
          })
        );
      } else {
        dispatch(atkJobData.actions.clearActiveObjective({ serial }));
      }
    }
  }

  if (message.activeAtomicTask) {
    const { task, type } = message.activeAtomicTask;
    // Clear on explicit DELETE or when server signals "no active" via id=0
    const normalized =
      type === UpdateType.UPDATE_TYPE_DELETE || !task?.id ? undefined : task;
    for (const serial of subscribedRobotSerials(
      getState().jobWebsocket.subscriptions
    )) {
      if (normalized) {
        dispatch(
          atkJobData.actions.setActiveTask({ serial, task: normalized })
        );
      } else {
        dispatch(atkJobData.actions.clearActiveTask({ serial }));
      }
    }
  }

  if (message.objectiveAssignment) {
    const { objectiveAssignment, type } = message.objectiveAssignment;
    if (!objectiveAssignment) {
      return;
    }
    if (type === UpdateType.UPDATE_TYPE_DELETE) {
      dispatch(
        atkJobData.actions.clearAssignment({
          objectiveId: objectiveAssignment.objectiveId,
        })
      );
    } else {
      dispatch(
        atkJobData.actions.setAssignment({ assignment: objectiveAssignment })
      );
    }
  }

  if (message.checklist) {
    const { checklist, type } = message.checklist;
    if (!checklist?.robotSerial) {
      return;
    }
    if (type === UpdateType.UPDATE_TYPE_DELETE) {
      dispatch(
        atkJobData.actions.clearChecklist({ serial: checklist.robotSerial })
      );
    } else {
      dispatch(
        atkJobData.actions.setChecklist({
          serial: checklist.robotSerial,
          checklist,
        })
      );
    }
  }

  if (message.checklistExemption) {
    const { checklistExemption, type } = message.checklistExemption;
    if (!checklistExemption?.robotSerial) {
      return;
    }
    if (type === UpdateType.UPDATE_TYPE_DELETE) {
      dispatch(
        atkJobData.actions.clearChecklistExemption({
          serial: checklistExemption.robotSerial,
        })
      );
    } else {
      dispatch(
        atkJobData.actions.setChecklistExemption({
          serial: checklistExemption.robotSerial,
          checklistExemption,
        })
      );
    }
  }

  if (message.intervention) {
    const { intervention, type } = message.intervention;
    if (!intervention) {
      return;
    }
    if (type === UpdateType.UPDATE_TYPE_DELETE) {
      dispatch(
        atkJobData.actions.clearGlobalIntervention({
          interventionId: intervention.id,
        })
      );
    } else {
      dispatch(atkJobData.actions.setGlobalIntervention({ intervention }));
    }
  }
};

const createJobWebsocketMiddlewareInstance = (): {
  middleware: Middleware;
  setSendMessage: (
    fn: (message: JobServiceSubscriptionRequest) => void
  ) => void;
  handleMessage: (message: JobServiceSubscriptionMessage) => void;
  setAuthenticated: (authenticated: boolean) => void;
} => {
  let sendFn: ((message: JobServiceSubscriptionRequest) => void) | undefined;
  let dispatch: SeedDispatch;
  let getState: () => MiddlewareState;

  const setSendMessage = (
    fn: (message: JobServiceSubscriptionRequest) => void
  ): void => {
    sendFn = fn;
  };

  const handleMessage = (message: JobServiceSubscriptionMessage): void => {
    routeMessage(message, dispatch, getState);
  };

  const setAuthenticated = (authenticated: boolean): void => {
    dispatch(jobWebsocketSlice.actions.setAuthenticated(authenticated));
    if (authenticated && sendFn) {
      const send = sendFn;
      forEachSubscription(
        getState().jobWebsocket.subscriptions,
        (target, id) => {
          send(buildWireMessage(target, id, SubscriptionRequestType.SUBSCRIBE));
          // Re-seed on (re)connect to refresh after any gap in the stream.
          // Fire-and-forget: seedSubscription never rejects (errors are swallowed).
          seedSubscription(target, id, dispatch);
        }
      );
    }
  };

  const middleware: Middleware<
    Record<string, never>,
    MiddlewareState,
    SeedDispatch
  > = (api) => {
    dispatch = api.dispatch;
    getState = api.getState;

    return (next) => (action) => {
      const prevSubs = api.getState().jobWebsocket.subscriptions;
      const result = next(action);
      const nextSubs = api.getState().jobWebsocket.subscriptions;

      const actionType = isAction(action) ? action.type : "";

      if (
        actionType === jobWebsocketSlice.actions.subscribe.type ||
        actionType === jobWebsocketSlice.actions.unsubscribe.type
      ) {
        const isAuthenticated = api.getState().jobWebsocket.authenticated;
        const send = sendFn;

        forEachSubscription(nextSubs, (target, id, count) => {
          // count was previously missing or 0, and has gone to 1. subscribe
          if (count === 1 && !prevSubs[target][id]) {
            // Seed the initial value over REST, independent of socket auth.
            // Fire-and-forget: seedSubscription never rejects (errors are swallowed).
            seedSubscription(target, id, api.dispatch);
            if (isAuthenticated && send) {
              send(
                buildWireMessage(target, id, SubscriptionRequestType.SUBSCRIBE)
              );
            }
          }
        });
        if (isAuthenticated && send) {
          forEachSubscription(prevSubs, (target, id) => {
            // count has gone to 0, or is missing. unsubscribe
            if (!nextSubs[target][id]) {
              send(
                buildWireMessage(
                  target,
                  id,
                  SubscriptionRequestType.UNSUBSCRIBE
                )
              );
            }
          });
        }
      }

      return result;
    };
  };

  return { middleware, setSendMessage, handleMessage, setAuthenticated };
};

export const jobWebsocketMiddlewareInstance =
  createJobWebsocketMiddlewareInstance();

export const jobWebsocketMiddleware = jobWebsocketMiddlewareInstance.middleware;
